/*=============================================================================
 util.ts - general utility functions

 - For debugging, attach "border border-pink-400" to the c_base.
 - Regex for the function call and the whole arguments:
    (c[C|L]0?o?)\(\s*([^)]+?)\s*\)
 - Keep this utility module with no dependencies

 (C) 2020 SpacetimeQ INC.
=============================================================================*/

const DEVELOPMENT_MODE: boolean =
  !process.env.NODE_ENV ||
   process.env.NODE_ENV === 'development';

export function isDev() {
  return DEVELOPMENT_MODE;
}

export function dbg(text: string) {  // debug text
  return DEVELOPMENT_MODE ? text : null;
}

export type TBrowserKind = 'chrome'|'safari'|'firefox'|'unknown';

export function addIf(
  cnd_if: Ifable, // if condition
  c_then: string  // then statement
) {
  return cnd_if ? c_then : '';
}

/**
 * 'block' may be not required; w-full not works with block
 */
export const showIf = (show: Ifable) => show ? 'block' : 'hidden';
export const hideIf = (hide: Ifable) => showIf(!hide);

// ----------------------------------------------------------------------------
// className Utilities - Purposes (mainly for tailwindcss' long list of classNames)
// ----------------------------------------------------------------------------
// 1. Combine list of strings, no need to care for the delimeter ' ' between strings.
// 2. Conditional expressions such as {mobile && 'ml-4'} can be seamlessly added.
//    (React className string cannot be concatenated with null or false expressions.)
// 3. Using the Object { className: ... } and the spread operator (...), "className=" can be removed.

type TCondClassF<T = TClassName> = (  // call signature
  c_base:  TClassName,   // base classname
  cnd_if:  Ifable,       // if the condition met
  c_then:  TClassName,   // concatenate
  c_else?: TClassName    // (optional) otherwise
) => T;

/**
 * className Conditional
 * Concatenate class names only if the provided condition is met
 * and return the className object so that it can be used with the spread syntax
 * - className={cC()}  --->  {...cCo()}
 */
export const cC: TCondClassF = (   // class with options (if-then-else)
  c_base, cnd_if, c_then, c_else
) => {
  const c_prefix = c_base ? c_base + ' ' : '';  // handle c_base Nullish case
  // const c_prefix = "border border-pink-400 " + (c_base ? c_base + ' ' : '');  // DEBUG
  if (cnd_if) return (c_prefix + c_then);
  if (c_else) return (c_prefix + c_else);  // if the third option was given
  return              c_base || '';
}

/**
 * className Conditional object
 */
export const cCo: TCondClassF<IClassNameObj> = (   // class with options (if-then-else)
  c_base, cnd_if, c_then, c_else
) => ({ className: cC(c_base, cnd_if, c_then, c_else) });

/**
 * className Conditional 0(no base) object
 */
export const cC0o = (
  cnd_if:  Ifable,      // if the condition met
  c_then:  TClassName,  // concatenate
  c_else?: TClassName   // (optional) otherwise
): IClassNameObj => ({ className: cnd_if ? c_then : c_else });

/**
 * class List
 * className={"m-1 text-gray-400 " + (cls || "")} can be written as:
 * className={cL('m-1', 'text-gray-400', cls)}
 * just boolean true value is filtered by the input type Maybe<string|false>
 */
export function cL(
  ...clss: readonly TClassName0[]  // rest parameters, list of classNames allowing null or boolean
): TClassName {
  // clss.push("border border-pink-400");  // DEBUG, remove 'readonly'
  return clss.reduce((cum, a) => a // check this add
    ? cum               // if valid previous (cumulative) string
      ? cum + ' ' + a   //   concatenate with the delimeter ' '
      : a               // no valid previous string
    : cum               // no valid a
  ) || undefined;       // Only undefined is allowed in nullish expressions in React
}

/**
 * class List with If (show or hide condition)
 */
export function cLIf(
  cnd_show: Ifable,        // only show the block if the condition met
  ...clss:  TClassName0[]  // rest parameters, list of classNames allowing null or boolean
): TClassName {
  return cL(showIf(cnd_show), ...clss);
}

/**
 * class List object: to omit 'className=' using spread attributes
 * className=""
 * {...cLo('m-1', 'text-gray-400', cls)}
 */
export function cLo(
  ...clss: TClassName0[]
): IClassNameObj {
  return { className: cL(...clss) };
}

/**
 * class List object with If (show or hide condition)
 */
export function cLoIf(
  cnd_show: Ifable,
  ...clss:  TClassName0[]
): IClassNameObj {
  return { className: cLIf(cnd_show, ...clss) };
}

// ----------------------------------------------------------------------------
/**
 * conditional string concatenation (cL's string version, no classNames)
 * The difference with cL is just the separating space between strings.
 */
export function cS(
  ...lstr: Maybe<string | false>[]  // rest parameters, list of strings allowing null or boolean
): stringU {
  return lstr.reduce((cum, a) => a  // check this add
    ? cum          // if valid previous (cumulative) string
      ? cum + a    //   concatenate with no delimeter
      : a          // no valid previous string
    : cum          // no valid a
  ) || undefined;  // Only undefined is allowed in nullish expressions in React
}
// ----------------------------------------------------------------------------

/**
 * number to leadingZero string
 * or use n.toString().padStart(2, "0");  // ES6
 */
export const lZ = (n: number) =>
  (n < 10)
    ? '0' + n
    : n.toString();  // leading Zero making a two digit number string

// const callifdef = (f, arg) => if (f && typeof f === "function") f(arg);

export function toggleValue<T = string>(val: T, a: T, b: T): T {
  return (val === a) ? b : a;
}

/**
 * warning format
 * @returns IError defined in 'types.d.ts' (like an action creator in Redux)
 */
export const warn0  = (message: string): IError => ({ code: 'WARN', message });

/**
 * for use as <a {...OPEN_NEW_WINDOW}>
 */
export const OPEN_NEW_WINDOW = {
  target: "_blank",
  rel:    "noopener noreferrer"
};

/**
 * error Format - IError builder
 * kind, code, message to an object format: IError object creator
 */
export const errorFmt = (
  kind:    IError["kind"],  // indexed access type
  code:    IError["code"],
  message: IError["message"]
) => ({ kind, code, message });

/**
 * Simple test version
 * * https://stackoverflow.com/questions/9847580/how-to-detect-safari-chrome-ie-firefox-and-opera-browser
 * @returns browser_name Chrome, Safari, Firefox, unknown
 */
export const detectBrowser = (): TBrowserKind => {
  const ua = navigator.userAgent.toLowerCase();
  if (ua.indexOf('safari') !== -1)
    return (ua.indexOf('chrome') > -1) ? 'chrome' : 'safari';
  if (ua.indexOf('firefox'))
    return 'firefox';
  return 'unknown';
}
// navigator.userAgent: "Mozilla/5.0 (Macintosh; Intel Mac OS X 10_14_5) AppleWebKit/537.36 (KHTML, like Gecko) Chrome/75.0.3770.142 Safari/537.36"

// interface Window { opera: any; };
// declare var window: Window;
// ||window.opera
/**
 * detect Mobile browser
 */
export const detectMobile = () => { // http://detectmobilebrowsers.com/
  let check = false;
// eslint-disable-next-line
  (function(a){if(/(android|bb\d+|meego).+mobile|avantgo|bada\/|blackberry|blazer|compal|elaine|fennec|hiptop|iemobile|ip(hone|od)|iris|kindle|lge |maemo|midp|mmp|mobile.+firefox|netfront|opera m(ob|in)i|palm( os)?|phone|p(ixi|re)\/|plucker|pocket|psp|series(4|6)0|symbian|treo|up\.(browser|link)|vodafone|wap|windows ce|xda|xiino/i.test(a)||/1207|6310|6590|3gso|4thp|50[1-6]i|770s|802s|a wa|abac|ac(er|oo|s\-)|ai(ko|rn)|al(av|ca|co)|amoi|an(ex|ny|yw)|aptu|ar(ch|go)|as(te|us)|attw|au(di|\-m|r |s )|avan|be(ck|ll|nq)|bi(lb|rd)|bl(ac|az)|br(e|v)w|bumb|bw\-(n|u)|c55\/|capi|ccwa|cdm\-|cell|chtm|cldc|cmd\-|co(mp|nd)|craw|da(it|ll|ng)|dbte|dc\-s|devi|dica|dmob|do(c|p)o|ds(12|\-d)|el(49|ai)|em(l2|ul)|er(ic|k0)|esl8|ez([4-7]0|os|wa|ze)|fetc|fly(\-|_)|g1 u|g560|gene|gf\-5|g\-mo|go(\.w|od)|gr(ad|un)|haie|hcit|hd\-(m|p|t)|hei\-|hi(pt|ta)|hp( i|ip)|hs\-c|ht(c(\-| |_|a|g|p|s|t)|tp)|hu(aw|tc)|i\-(20|go|ma)|i230|iac( |\-|\/)|ibro|idea|ig01|ikom|im1k|inno|ipaq|iris|ja(t|v)a|jbro|jemu|jigs|kddi|keji|kgt( |\/)|klon|kpt |kwc\-|kyo(c|k)|le(no|xi)|lg( g|\/(k|l|u)|50|54|\-[a-w])|libw|lynx|m1\-w|m3ga|m50\/|ma(te|ui|xo)|mc(01|21|ca)|m\-cr|me(rc|ri)|mi(o8|oa|ts)|mmef|mo(01|02|bi|de|do|t(\-| |o|v)|zz)|mt(50|p1|v )|mwbp|mywa|n10[0-2]|n20[2-3]|n30(0|2)|n50(0|2|5)|n7(0(0|1)|10)|ne((c|m)\-|on|tf|wf|wg|wt)|nok(6|i)|nzph|o2im|op(ti|wv)|oran|owg1|p800|pan(a|d|t)|pdxg|pg(13|\-([1-8]|c))|phil|pire|pl(ay|uc)|pn\-2|po(ck|rt|se)|prox|psio|pt\-g|qa\-a|qc(07|12|21|32|60|\-[2-7]|i\-)|qtek|r380|r600|raks|rim9|ro(ve|zo)|s55\/|sa(ge|ma|mm|ms|ny|va)|sc(01|h\-|oo|p\-)|sdk\/|se(c(\-|0|1)|47|mc|nd|ri)|sgh\-|shar|sie(\-|m)|sk\-0|sl(45|id)|sm(al|ar|b3|it|t5)|so(ft|ny)|sp(01|h\-|v\-|v )|sy(01|mb)|t2(18|50)|t6(00|10|18)|ta(gt|lk)|tcl\-|tdg\-|tel(i|m)|tim\-|t\-mo|to(pl|sh)|ts(70|m\-|m3|m5)|tx\-9|up(\.b|g1|si)|utst|v400|v750|veri|vi(rg|te)|vk(40|5[0-3]|\-v)|vm40|voda|vulc|vx(52|53|60|61|70|80|81|83|85|98)|w3c(\-| )|webc|whit|wi(g |nc|nw)|wmlb|wonu|x700|yas\-|your|zeto|zte\-/i.test(a.substr(0,4))) check = true;})(navigator.userAgent||navigator.vendor);
  return check;
}

export const browserType = detectBrowser();
export const isSafari = browserType === 'safari';  // initialized

/**
 * https://phrase.com/blog/posts/detecting-a-users-locale/
 */
export function getBrowserLocales(options = {}) {
  const defaultOptions = {
    languageCodeOnly: true,
  };

  const opt = {
    ...defaultOptions,
    ...options,
  };

  const browserLocales =
    navigator.languages === undefined
      ? [navigator.language]
      : navigator.languages;

  if (!browserLocales) {
    return undefined;
  }

  return browserLocales.map(locale => {
    const trimmedLocale = locale.trim();

    return opt.languageCodeOnly
      ? trimmedLocale.split(/-|_/)[0]
      : trimmedLocale;
  });
}

/**
  * Plain text in template string to HTML
  */
export const replaceCRtoHTML = (text: string) => text.replace(/\r?\n|\r/g, "<br/>");

/**
  * Template string inserts CR for each line. To remove them use this function.
  */
export const removeCR = (html: string) => html.replace(/\r?\n|\r/g, "");
